import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOverview, Overview, PatientRow } from "../../../components/Admin/adminApi";
import { formatWhen, timeAgo, useAdminShell } from "../../../components/Admin/adminShell";
import styles from "./AdminOverview.module.css";

type SortKey = "email" | "tags" | "lastActive" | "sessionsThisWeek" | "activeDays28" | "appVersion";
type Sort = { key: SortKey | "default"; desc: boolean };

const APP_DETAILS: Record<string, { track: string; major: string }> = {
  mRehab: { track: "Play open testing", major: "1.x" },
  "mRehab v2": { track: "Play internal testing", major: "2.x" },
};

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "email", label: "Patient" },
  { key: "tags", label: "Tags" },
  { key: "lastActive", label: "Last active" },
  { key: "sessionsThisWeek", label: "This week" },
  { key: "activeDays28", label: "Active days (28d)" },
  { key: "appVersion", label: "App" },
];

function sortValue(row: PatientRow, key: SortKey): string | number {
  switch (key) {
    case "lastActive":
      return row.lastActive ? new Date(row.lastActive).getTime() : 0;
    case "sessionsThisWeek":
      return row.sessionsThisWeek;
    case "activeDays28":
      return row.activeDays28;
    case "tags":
      return row.tags.join(", ").toLowerCase();
    case "appVersion":
      return row.appVersion || "";
    default:
      return row.email;
  }
}

function PatientTable({ rows, onOpen }: { rows: PatientRow[]; onOpen: (email: string) => void }) {
  // Default: busiest this week first, then the most regular users, then most recent.
  const [sort, setSort] = useState<Sort>({ key: "default", desc: true });
  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      if (sort.key === "default") {
        return (
          b.sessionsThisWeek - a.sessionsThisWeek ||
          b.activeDays28 - a.activeDays28 ||
          (sortValue(b, "lastActive") as number) - (sortValue(a, "lastActive") as number)
        );
      }
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sort.desc ? -cmp : cmp;
    });
    return out;
  }, [rows, sort]);

  if (rows.length === 0) return <p className={styles.empty}>No patients match.</p>;

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th key={c.key} aria-sort={sort.key === c.key ? (sort.desc ? "descending" : "ascending") : "none"}>
                <button
                  type="button"
                  className={styles.sortBtn}
                  onClick={() => setSort((s) => ({ key: c.key, desc: s.key === c.key ? !s.desc : c.key !== "email" && c.key !== "tags" }))}
                >
                  {c.label}
                  {sort.key === c.key ? (sort.desc ? " ↓" : " ↑") : ""}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.email} className={styles.row} onClick={() => onOpen(r.email)}>
              <td>
                <button type="button" className={styles.emailBtn} onClick={(e) => { e.stopPropagation(); onOpen(r.email); }}>
                  {r.email}
                </button>
                {r.name && <span className={styles.name}>{r.name}</span>}
              </td>
              <td>
                {r.tags.map((t) => (
                  <span key={t} className="ui-badge ui-badge--neutral">
                    {t}
                  </span>
                ))}
              </td>
              <td title={r.lastActive || undefined}>{r.lastActive ? timeAgo(r.lastActive) : "Never"}</td>
              <td>{r.sessionsThisWeek}</td>
              <td>{r.activeDays28}</td>
              <td data-older={String(r.olderVersion)} className={r.olderVersion ? styles.older : undefined}>
                {r.appVersion || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A collapsed list of rows kept out of the tiles and main table. */
function HiddenRows({ title, rows, note, onOpen }: { title: string; rows: PatientRow[]; note: string; onOpen: (email: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <section className={styles.tests}>
      <button type="button" className="ui-btn ui-btn--sm ui-btn--ghost" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} {title} ({rows.length})
      </button>
      {open && (
        <div className="ui-card">
          <p className={styles.note}>{note}</p>
          <PatientTable rows={rows} onOpen={onOpen} />
        </div>
      )}
    </section>
  );
}

/** Admin landing page: this week's activity, app versions, and every real patient. */
export default function AdminOverview() {
  const navigate = useNavigate();
  const { refreshKey, reportUpdated } = useAdminShell();
  const [tag, setTag] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState<Overview | null>(null);
  const [knownTags, setKnownTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    getOverview({ tag: tag || undefined, includeTest })
      .then((o) => {
        if (!live) return;
        setData(o);
        setError(null);
        reportUpdated(o.updatedAt);
        // Keep every tag ever seen so a filter never removes its own option.
        setKnownTags((prev) => {
          const all = new Set(prev);
          [...o.patients, ...o.testAccounts].forEach((r) => r.tags.forEach((t) => all.add(t)));
          return Array.from(all).sort((a, b) => a.localeCompare(b));
        });
      })
      .catch((e) => live && setError((e as Error).message))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [tag, includeTest, refreshKey, reportUpdated]);

  const open = (email: string) => navigate(`/admin/patients/${encodeURIComponent(email)}`);
  const t = data?.tiles;

  return (
    <div className={styles.page}>
      <div className={styles.controls}>
        <label className={styles.control}>
          <span className="ui-label">Tag</span>
          <select className="ui-input" aria-label="Tag" value={tag} onChange={(e) => setTag(e.target.value)}>
            <option value="">All patients</option>
            {knownTags.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.check}>
          <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} />
          Show test accounts in counts
        </label>
        {loading && <span className="ui-spinner" aria-label="Loading" />}
      </div>

      {error && (
        <p className="ui-banner ui-banner--error" role="alert">
          {error}
        </p>
      )}

      {t && (
        <div className="ui-stat-row">
          <div className="ui-stat-tile">
            <span className="ui-stat-value">{`${t.activeThisWeek} / ${t.patients}`}</span>
            <span className="ui-section-label">Patients active this week</span>
          </div>
          <div className="ui-stat-tile">
            <span className="ui-stat-value">{t.sessionsThisWeek}</span>
            <span className="ui-section-label">Sessions this week</span>
            <span className="ui-stat-sub">vs {t.sessionsLastWeek} last week</span>
          </div>
          {t.apps.map((a) => (
            <div key={a.app} className="ui-stat-tile">
              <span className="ui-stat-value">{`${a.app} ${a.newestVersion}`}</span>
              <span className="ui-section-label">Newest seen · {APP_DETAILS[a.app]?.track || a.app}</span>
              <span className="ui-stat-sub">
                {`${a.olderVersionPatients} of ${a.patients} on an older ${APP_DETAILS[a.app]?.major || ""}`}
              </span>
            </div>
          ))}
          <div className="ui-stat-tile">
            <span className="ui-stat-value">{timeAgo(t.lastUpload)}</span>
            <span className="ui-section-label">Last upload</span>
            <span className="ui-stat-sub">{formatWhen(t.lastUpload)}</span>
          </div>
        </div>
      )}

      {data && (
        <div className="ui-card">
          <PatientTable rows={data.patients} onOpen={open} />
        </div>
      )}

      {data && (
        <HiddenRows
          title="Test accounts"
          rows={data.testAccounts}
          note='Not counted above unless "Show test accounts in counts" is on. Clinician and admin accounts are never listed.'
          onOpen={open}
        />
      )}

      {data && (
        <HiddenRows
          title="Old session names (no account)"
          rows={data.noAccount || []}
          note="Session rows whose username matches no account: deleted accounts, or early app builds that saved a name instead of an email. Never counted."
          onOpen={open}
        />
      )}
    </div>
  );
}
